import { apiDownload, apiFetch, apiFetchBlob, apiUploadFormData } from "@/auth/api";
import type {
  AuditRecipient,
  Caixa,
  Conferencia,
  ConferirPayload,
  ConferirResult,
  ContagemItem,
  Devolucao,
  DevolucaoCreate,
  Dispensacao,
  DispensacaoCreate,
  FefoSugestao,
  LivroEspelho,
  Local,
  Medicamento,
  Medico,
  NFDetalhe,
  NFEntrada,
  Paciente,
  ReposicaoCaixa,
  Requisicao,
  RequisicaoCreate,
  StatusDevolucao,
  StatusRequisicao,
  Unidade,
  UploadPreview,
  UploadResult,
} from "@/types/api";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

// ---------- Medicamentos ----------
export function useMedicamentos(filtros: { q?: string; categoria?: string; portaria_344?: boolean } = {}) {
  const params = new URLSearchParams();
  if (filtros.q) params.set("q", filtros.q);
  if (filtros.categoria) params.set("categoria", filtros.categoria);
  if (filtros.portaria_344 !== undefined) params.set("portaria_344", String(filtros.portaria_344));
  return useQuery<Medicamento[]>({
    queryKey: ["medicamentos", filtros],
    queryFn: () => apiFetch<Medicamento[]>(`/medicamentos?${params.toString()}`),
  });
}

// ---------- Movimentações manuais de estoque (sem NF) ----------
export interface EntradaManualPayload {
  medicamento_id: string;
  local_id: string;
  numero_lote: string;
  data_validade: string;
  quantidade: number;
  motivo: string;
  data_fabricacao?: string | null;
  observacao?: string | null;
}

export interface SaidaManualPayload {
  lote_id: string;
  local_id: string;
  quantidade: number;
  motivo: string;
  observacao: string; // justificativa obrigatória
}

export interface MovimentacaoManualRecente {
  id: string;
  ts: string;
  tipo: "entrada" | "saida" | "estorno";
  estornado: boolean;
  usuario: string | null;
  medicamento_codigo: string;
  medicamento_descricao: string;
  local: string;
  numero_lote: string;
  quantidade: number;
  motivo: string;
  observacao: string | null;
}

export function useEntradaManual() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: EntradaManualPayload) =>
      apiFetch("/estoque/entrada-manual", { method: "POST", json: body }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["estoque", "movimentacoes-manuais"] });
      qc.invalidateQueries({ queryKey: ["lotes-em-estoque"] });
      qc.invalidateQueries({ queryKey: ["kits"] });
    },
  });
}

export function useSaidaManual() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: SaidaManualPayload) =>
      apiFetch("/estoque/saida-manual", { method: "POST", json: body }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["estoque", "movimentacoes-manuais"] });
      qc.invalidateQueries({ queryKey: ["lotes-em-estoque"] });
      qc.invalidateQueries({ queryKey: ["kits"] });
    },
  });
}

export function useMovimentacoesManuais() {
  return useQuery<MovimentacaoManualRecente[]>({
    queryKey: ["estoque", "movimentacoes-manuais"],
    queryFn: () => apiFetch<MovimentacaoManualRecente[]>("/estoque/movimentacoes-manuais"),
  });
}

export function useEstornarMovimentacao() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { audit_id: string; justificativa: string }) =>
      apiFetch("/estoque/estorno", { method: "POST", json: body }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["estoque", "movimentacoes-manuais"] });
      qc.invalidateQueries({ queryKey: ["lotes-em-estoque"] });
      qc.invalidateQueries({ queryKey: ["kits"] });
      qc.invalidateQueries({ queryKey: ["livro"] });
    },
  });
}

export function useEditarMedicamento() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: Partial<Medicamento> }) =>
      apiFetch<Medicamento>(`/medicamentos/${id}`, { method: "PATCH", json: patch }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["medicamentos"] }),
  });
}

export interface MedicamentoCreatePayload {
  codigo_interno?: string | null;
  codigo_barras_ean?: string | null;
  codigo_anvisa?: string | null;
  descricao: string;
  principio_ativo?: string | null;
  apresentacao?: string | null;
  unidade_medida?: string | null;
  categoria?: string;
  portaria_344?: boolean;
  tipo_receituario_344?: string | null;
  estoque_minimo?: number;
}

export function useCriarMedicamento() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: MedicamentoCreatePayload) =>
      apiFetch<Medicamento>("/medicamentos", { method: "POST", json: body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["medicamentos"] }),
  });
}

// ---------- Pacientes ----------
export function usePacientes(q: string) {
  return useQuery<Paciente[]>({
    queryKey: ["pacientes", q],
    queryFn: () => apiFetch<Paciente[]>(`/pacientes?q=${encodeURIComponent(q)}`),
    enabled: q.length >= 3,
  });
}

export interface PacienteCreatePayload {
  cpf: string;
  numero_prontuario: string;
  nome: string;
  data_nascimento?: string | null;
  convenio?: string | null;
  numero_carteirinha?: string | null;
}

export function useCriarPaciente() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: PacienteCreatePayload) =>
      apiFetch<Paciente>("/pacientes", { method: "POST", json: body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["pacientes"] }),
  });
}

// ---------- Médicos ----------
export function useMedicos(q: string) {
  return useQuery<Medico[]>({
    queryKey: ["medicos", q],
    queryFn: () => apiFetch<Medico[]>(`/medicos?q=${encodeURIComponent(q)}`),
    enabled: q.length >= 2,
  });
}

export interface MedicoCreatePayload {
  nome: string;
  crm: string;
  uf_crm?: string | null;
  especialidade?: string | null;
  origem?: string;
}

export function useCriarMedico() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: MedicoCreatePayload) =>
      apiFetch<Medico>("/medicos", { method: "POST", json: body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["medicos"] }),
  });
}

// ---------- Unidades ----------
export function useUnidades() {
  return useQuery<Unidade[]>({
    queryKey: ["admin", "unidades"],
    queryFn: () => apiFetch<Unidade[]>("/admin/unidades"),
  });
}

export function useCriarUnidade() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Partial<Unidade>) =>
      apiFetch<Unidade>("/admin/unidades", { method: "POST", json: body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin", "unidades"] }),
  });
}

export function useAtualizarUnidade() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: Partial<Unidade> }) =>
      apiFetch<Unidade>(`/admin/unidades/${id}`, { method: "PATCH", json: patch }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin", "unidades"] }),
  });
}

// ---------- Locais ----------
export function useLocais(unidade_id?: string) {
  const qs = unidade_id ? `?unidade_id=${unidade_id}` : "";
  return useQuery<Local[]>({
    queryKey: ["admin", "locais", unidade_id],
    queryFn: () => apiFetch<Local[]>(`/admin/locais${qs}`),
  });
}

export function useCriarLocal() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Partial<Local>) =>
      apiFetch<Local>("/admin/locais", { method: "POST", json: body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin", "locais"] }),
  });
}

// ---------- Audit recipients ----------
export function useAuditRecipients() {
  return useQuery<AuditRecipient[]>({
    queryKey: ["admin", "audit-recipients"],
    queryFn: () => apiFetch<AuditRecipient[]>("/admin/audit-recipients"),
  });
}

export function useAddAuditRecipient() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { email: string; nome?: string }) =>
      apiFetch<AuditRecipient>("/admin/audit-recipients", { method: "POST", json: body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin", "audit-recipients"] }),
  });
}

// ---------- Notas Fiscais ----------
export function useNFs() {
  return useQuery<NFEntrada[]>({
    queryKey: ["nfs"],
    queryFn: () => apiFetch<NFEntrada[]>("/nfs"),
  });
}

export function useNFDetalhe(id: string | null) {
  return useQuery<NFDetalhe>({
    queryKey: ["nfs", id],
    queryFn: () => apiFetch<NFDetalhe>(`/nfs/${id}`),
    enabled: !!id,
  });
}

function xmlForm(file: File): FormData {
  const fd = new FormData();
  fd.append("xml", file);
  return fd;
}

export function useUploadNFPreview() {
  return useMutation({
    mutationFn: (file: File) =>
      apiUploadFormData<UploadPreview>("/nfs/upload/preview", xmlForm(file)),
  });
}

export function useUploadNF() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (file: File) =>
      apiUploadFormData<UploadResult>("/nfs/upload", xmlForm(file)),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["nfs"] }),
  });
}

export function useConferencia(nfId: string | null) {
  return useQuery<Conferencia>({
    queryKey: ["nfs", nfId, "conferencia"],
    queryFn: () => apiFetch<Conferencia>(`/nfs/${nfId}/conferencia`),
    enabled: !!nfId,
  });
}

export function useConferirNF() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ nfId, payload }: { nfId: string; payload: ConferirPayload }) =>
      apiFetch<ConferirResult>(`/nfs/${nfId}/conferir`, { method: "POST", json: payload }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["nfs"] }),
  });
}

export function useRemoveAuditRecipient() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch<void>(`/admin/audit-recipients/${id}`, { method: "DELETE" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin", "audit-recipients"] }),
  });
}

// ---------- Requisições ----------
export function useRequisicoes(filters: { status?: StatusRequisicao } = {}) {
  const qs = new URLSearchParams();
  if (filters.status) qs.set("status", filters.status);
  return useQuery<Requisicao[]>({
    queryKey: ["requisicoes", filters],
    queryFn: () => apiFetch<Requisicao[]>(`/requisicoes?${qs.toString()}`),
  });
}

export function useFefoSugestao(
  medicamento_id: string | null,
  local_id: string | null,
) {
  return useQuery<FefoSugestao>({
    queryKey: ["fefo", medicamento_id, local_id],
    queryFn: () =>
      apiFetch<FefoSugestao>(
        `/requisicoes/fefo-sugestao?medicamento_id=${medicamento_id}&local_id=${local_id}`,
      ),
    enabled: !!medicamento_id && !!local_id,
    retry: false,
  });
}

export function useCriarRequisicao() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: RequisicaoCreate) =>
      apiFetch<{ id: string; numero: string }>("/requisicoes", { method: "POST", json: body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["requisicoes"] }),
  });
}

export function useAtenderRequisicao() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch<{ id: string; status: string }>(`/requisicoes/${id}/atender`, { method: "POST" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["requisicoes"] }),
  });
}

export interface AtenderItemPayload {
  reqId: string;
  itemId: string;
  qtd: number;
  numero_lote: string;
  data_validade?: string | null;
  observacao?: string | null;
}

export function useAtenderItemRequisicao() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ reqId, itemId, ...body }: AtenderItemPayload) =>
      apiFetch(`/requisicoes/${reqId}/itens/${itemId}/atender`, { method: "POST", json: body }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["requisicoes"] });
      qc.invalidateQueries({ queryKey: ["lotes-em-estoque"] });
    },
  });
}

export function useCancelarRequisicao() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch<{ id: string; status: string }>(`/requisicoes/${id}/cancelar`, { method: "POST" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["requisicoes"] }),
  });
}

// ---------- Dispensações ----------
export function useDispensacoes(paciente_id?: string) {
  const qs = paciente_id ? `?paciente_id=${paciente_id}` : "";
  return useQuery<Dispensacao[]>({
    queryKey: ["dispensacoes", paciente_id ?? null],
    queryFn: () => apiFetch<Dispensacao[]>(`/dispensacoes${qs}`),
  });
}

export function useAnularDispensacao() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, motivo }: { id: string; motivo: string }) =>
      apiFetch(`/dispensacoes/${id}/anular`, { method: "POST", json: { motivo } }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["dispensacoes"] });
      qc.invalidateQueries({ queryKey: ["lotes-em-estoque"] });
      qc.invalidateQueries({ queryKey: ["livro"] });
    },
  });
}

export interface DispensacaoEnfermagemPayload {
  posto_id: string;
  paciente_id: string;
  itens: { medicamento_id: string; quantidade: number }[];
  farmacia_solicitada_id?: string | null;
  medico_prescritor_nome?: string | null;
  crm_prescritor?: string | null;
  numero_notificacao_344?: string | null;
  retencao_receita?: boolean;
}

export function useCriarDispensacaoEnfermagem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: DispensacaoEnfermagemPayload) =>
      apiFetch<{ direto: unknown; solicitada: unknown }>("/dispensacoes/enfermagem",
        { method: "POST", json: body }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["dispensacoes"] });
      qc.invalidateQueries({ queryKey: ["lotes-em-estoque"] });
    },
  });
}

export interface OcrCandidato {
  medicamento_id: string;
  codigo: string;
  descricao: string;
  score: number;
  match: string[];
}
export interface OcrResultado {
  cpf: string | null;
  crm: string | null;
  medico: string | null;
  medicamentos: OcrCandidato[];
  texto_ocr: string;
}

export function useLerPrescricao() {
  return useMutation({
    mutationFn: (file: File) => {
      const fd = new FormData();
      fd.append("arquivo", file);
      return apiUploadFormData<OcrResultado>("/dispensacoes/ler-prescricao", fd);
    },
  });
}

export function useLiberarDispensacao() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiFetch(`/dispensacoes/${id}/liberar`, { method: "POST" }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["dispensacoes"] });
      qc.invalidateQueries({ queryKey: ["lotes-em-estoque"] });
      qc.invalidateQueries({ queryKey: ["livro"] });
    },
  });
}

export function useRecusarDispensacao() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, motivo }: { id: string; motivo: string }) =>
      apiFetch(`/dispensacoes/${id}/recusar`, { method: "POST", json: { motivo } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["dispensacoes"] }),
  });
}

export function useCriarDispensacao() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: DispensacaoCreate) =>
      apiFetch<{ id: string; numero: string }>("/dispensacoes", { method: "POST", json: body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["dispensacoes"] }),
  });
}

// ---------- Devoluções ----------
export function useDevolucoes(filters: { status?: StatusDevolucao } = {}) {
  const qs = filters.status ? `?status=${filters.status}` : "";
  return useQuery<Devolucao[]>({
    queryKey: ["devolucoes", filters],
    queryFn: () => apiFetch<Devolucao[]>(`/devolucoes${qs}`),
  });
}

export function useCriarDevolucao() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: DevolucaoCreate) =>
      apiFetch<{ id: string; numero: string; status: string }>("/devolucoes", { method: "POST", json: body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["devolucoes"] }),
  });
}

export function useConfirmarDevolucao() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch<{ id: string; status: string }>(`/devolucoes/${id}/confirmar`, { method: "POST" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["devolucoes"] }),
  });
}

export function useCancelarDevolucao() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch<{ id: string; status: string }>(`/devolucoes/${id}/cancelar`, { method: "POST" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["devolucoes"] }),
  });
}

// ---------- Lotes em estoque ----------
export interface LoteEmEstoque {
  lote_id: string;
  medicamento_id: string;
  medicamento_codigo: string;
  medicamento_descricao: string;
  medicamento_categoria: string;
  numero_lote: string;
  data_validade: string;
  status_lote: string;
  quantidade: number;
}

export function useLotesEmEstoque(local_id: string | null, medicamento_id?: string | null) {
  const qs = medicamento_id ? `&medicamento_id=${medicamento_id}` : "";
  return useQuery<LoteEmEstoque[]>({
    queryKey: ["lotes-em-estoque", local_id, medicamento_id ?? null],
    queryFn: () => apiFetch<LoteEmEstoque[]>(`/lotes/em-estoque?local_id=${local_id}${qs}`),
    enabled: !!local_id,
  });
}

// ---------- Scanner lookups ----------
export async function lookupMedicamentoPorEan(ean: string): Promise<Medicamento> {
  return apiFetch<Medicamento>(`/medicamentos/por-ean/${encodeURIComponent(ean)}`);
}

export interface LoteEtiquetaLookup {
  lote_id: string;
  medicamento_id: string;
  medicamento_codigo: string;
  medicamento_descricao: string;
  medicamento_categoria: string;
  medicamento_portaria_344: boolean;
  medicamento_ean: string | null;
  numero_lote: string;
  data_validade: string;
  status_lote: string;
}

export async function lookupLotePorEtiqueta(codigo: string): Promise<LoteEtiquetaLookup> {
  return apiFetch<LoteEtiquetaLookup>(`/lotes/por-etiqueta/${encodeURIComponent(codigo)}`);
}

// ---------- Kits / Caixas ----------
export function useCaixas() {
  return useQuery<Caixa[]>({ queryKey: ["kits"], queryFn: () => apiFetch<Caixa[]>("/kits") });
}

export function useReposicaoCaixa(localId: string | null) {
  return useQuery<ReposicaoCaixa>({
    queryKey: ["kits", localId, "reposicao"],
    queryFn: () => apiFetch<ReposicaoCaixa>(`/kits/${localId}/reposicao`),
    enabled: !!localId,
  });
}

export function useReporCaixa() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ localId, origem_local_id, itens }: {
      localId: string;
      origem_local_id: string;
      itens: Array<{ medicamento_id: string; quantidade: number }>;
    }) => apiFetch(`/kits/${localId}/repor`, { method: "POST", json: { origem_local_id, itens } }),
    onSuccess: (_d, v) => {
      qc.invalidateQueries({ queryKey: ["kits"] });
      qc.invalidateQueries({ queryKey: ["kits", v.localId, "reposicao"] });
    },
  });
}

export interface ComposicaoItem {
  id?: string;
  medicamento_id: string;
  codigo_interno?: string;
  descricao?: string;
  qtd_alvo: number;
}

export function useComposicao(tipoKit: string) {
  return useQuery<ComposicaoItem[]>({
    queryKey: ["kits", "composicao", tipoKit],
    queryFn: () => apiFetch<ComposicaoItem[]>(`/kits/composicao/${tipoKit}`),
  });
}

export function useSalvarComposicao() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ tipoKit, itens }: { tipoKit: string; itens: Array<{ medicamento_id: string; qtd_alvo: number }> }) =>
      apiFetch(`/kits/composicao/${tipoKit}`, { method: "PUT", json: itens }),
    onSuccess: (_d, v) => {
      qc.invalidateQueries({ queryKey: ["kits", "composicao", v.tipoKit] });
      qc.invalidateQueries({ queryKey: ["kits"] });
    },
  });
}

// ---------- Livro de controlados ----------
export function useLivroEspelho(ano: number, mes: number, unidade_id?: string) {
  const qs = unidade_id ? `&unidade_id=${unidade_id}` : "";
  return useQuery<LivroEspelho>({
    queryKey: ["livro", "espelho", ano, mes, unidade_id ?? null],
    queryFn: () => apiFetch<LivroEspelho>(`/livro-controlados/espelho?ano=${ano}&mes=${mes}${qs}`),
  });
}

export function useContagem(unidade_id?: string) {
  const qs = unidade_id ? `?unidade_id=${unidade_id}` : "";
  return useQuery<ContagemItem[]>({
    queryKey: ["livro", "contagem", unidade_id ?? null],
    queryFn: () => apiFetch<ContagemItem[]>(`/livro-controlados/contagem${qs}`),
  });
}

// ---------- Relatórios (download PDF/CSV) ----------
export type FormatoRelatorio = "pdf" | "csv";

export function downloadRelatorioLivro(p: {
  unidade_id: string; ano: number; mes: number; formato: FormatoRelatorio;
}): Promise<void> {
  return apiDownload(
    `/relatorios/livro?unidade_id=${p.unidade_id}&ano=${p.ano}&mes=${p.mes}&formato=${p.formato}`,
    `livro-controlados-${p.ano}-${String(p.mes).padStart(2, "0")}.${p.formato}`,
  );
}

export function downloadRelatorioBspo(p: {
  unidade_id: string; ano: number; trimestre?: number | null; formato: FormatoRelatorio;
}): Promise<void> {
  const tri = p.trimestre ? `&trimestre=${p.trimestre}` : "";
  return apiDownload(
    `/relatorios/bspo?unidade_id=${p.unidade_id}&ano=${p.ano}${tri}&formato=${p.formato}`,
    `bspo-${p.ano}${p.trimestre ? `-T${p.trimestre}` : ""}.${p.formato}`,
  );
}

export function downloadRelatorioContagem(p: {
  unidade_id?: string | null; formato: FormatoRelatorio;
}): Promise<void> {
  const uni = p.unidade_id ? `unidade_id=${p.unidade_id}&` : "";
  return apiDownload(
    `/relatorios/contagem?${uni}formato=${p.formato}`,
    `contagem-controlados.${p.formato}`,
  );
}

export function downloadRelatorioDispensacaoAltoCusto(p: {
  inicio: string; fim: string; unidade_id?: string | null; formato: FormatoRelatorio;
}): Promise<void> {
  const uni = p.unidade_id ? `unidade_id=${p.unidade_id}&` : "";
  return apiDownload(
    `/relatorios/dispensacao-alto-custo?inicio=${p.inicio}&fim=${p.fim}&${uni}formato=${p.formato}`,
    `dispensacao-alto-custo-${p.inicio}-a-${p.fim}.${p.formato}`,
  );
}

// ---------- Prescrição anexa (dispensação) ----------
export function useAnexarPrescricao() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ dispensacaoId, file }: { dispensacaoId: string; file: File }) => {
      const fd = new FormData();
      fd.append("arquivo", file);
      return apiUploadFormData(`/dispensacoes/${dispensacaoId}/prescricao`, fd);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["dispensacoes"] }),
  });
}

/** Abre o anexo da prescrição numa nova aba (stream autenticado → blob URL). */
export async function abrirPrescricao(dispensacaoId: string): Promise<void> {
  const { blob } = await apiFetchBlob(`/dispensacoes/${dispensacaoId}/prescricao`);
  const url = URL.createObjectURL(blob);
  window.open(url, "_blank", "noopener");
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}
